import { sumBy } from 'es-toolkit';

import { T } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal/index';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';

export interface SuspendedPositionsNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  closedSymbols: { symbol: string; displayShortName: string; count: number }[];
  suspendedSymbols: { symbol: string; displayShortName: string; count: number }[];
}

export const SuspendedPositionsNoticeModal = ({
  isOpen,
  onClose,
  onExit,
  closedSymbols,
  suspendedSymbols,
}: SuspendedPositionsNoticeModalProps) => {
  const isPartial = closedSymbols.length > 0;

  const closedCount = sumBy(closedSymbols, (s) => s.count);
  const totalCount = closedCount + sumBy(suspendedSymbols, (s) => s.count);

  const closedText = closedSymbols.map((s) => `${s.displayShortName} (${s.count})`).join(', ');
  const suspendedText = suspendedSymbols.map((s) => `${s.displayShortName} (${s.count})`).join(', ');

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit} mode='bottomSheet'>
      <Modal.Header>
        <Modal.Title
          label={
            isPartial ? (
              <T id='Position_close.r260318.Close_all_tradfi_title' values={{ closedCount, totalCount }} />
            ) : (
              <T id='Position_close.r260318.Close_all_tradfi_title_no_closed' />
            )
          }
        />
      </Modal.Header>
      <Modal.Content>
        <div className={vstack({ gap: 20, alignItems: 'stretch' })}>
          {isPartial && (
            <div className={hstack({ gap: 12, alignItems: 'center' })}>
              <CheckCircleIcon size={24} className={css({ color: 'content.accent.info' })} />
              <div className={vstack({ gap: 2, alignItems: 'flex-start' })}>
                <span className={css({ textStyle: 'Title/XS', color: 'content.default.level-5' })}>
                  <T id='Position_close.r260318.Close_all_tradfi_closed' />
                </span>
                <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
                  {closedText}
                </span>
              </div>
            </div>
          )}
          <div
            className={vstack({
              gap: 8,
              alignItems: 'stretch',
              borderTopWidth: isPartial ? '1px' : undefined,
              borderTopStyle: isPartial ? 'solid' : undefined,
              borderColor: isPartial ? 'border.default.level-1' : undefined,
              pt: isPartial ? 16 : 0,
            })}
          >
            <div className={vstack({ gap: 2, alignItems: 'flex-start' })}>
              <span className={css({ textStyle: 'Title/XS', color: 'content.default.level-5' })}>
                <T id='Position_close.r260318.Close_all_tradfi_not_closed' />
              </span>
              <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
                {suspendedText}
              </span>
            </div>
            <div className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
              <T id='Position_close.r260318.Close_all_tradfi_outside_hours' />
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button size='large' label={<T id='Trade.r250401.Got_it' />} onClick={onClose} />
      </Modal.Footer>
    </Modal.Root>
  );
};
